/**
 * Viewport Transition Manager
 *
 * Manages smooth transitions between responsive breakpoints in browser inspect mode.
 * Preserves component relationships and applies CSS-first viewport switching.
 *
 * Architecture: Event-driven transition system with animation scheduling
 * and state synchronization across all viewport instances.
 */

import { Component, Position, Size } from "@/types";
import { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
import {
  calculateResponsivePosition,
  calculateResponsiveSize,
  generateResponsiveCSSProperties,
  applyResponsiveConstraints,
} from "@/utils/responsive-position-calculator";

export interface ViewportTransitionConfig {
  fromViewport: ResponsiveBreakpoint;
  toViewport: ResponsiveBreakpoint;
  components: Component[];
  duration?: number; // milliseconds
  easing?: 'linear' | 'ease-in-out' | 'ease-out' | 'ease-in';
  preserveRelations?: boolean;
}

export interface TransitionState {
  isTransitioning: boolean;
  progress: number; // 0 to 1
  fromViewport: ResponsiveBreakpoint | null;
  toViewport: ResponsiveBreakpoint | null;
  startTime: number;
  duration: number;
}

export interface ComponentTransitionData {
  componentId: string;
  startPosition: Position;
  endPosition: Position;
  startSize: Size;
  endSize: Size;
  currentPosition: Position;
  currentSize: Size;
}

/**
 * Viewport Transition Manager
 *
 * Coordinates smooth viewport transitions with CSS animations and
 * state synchronization.
 */
export class ViewportTransitionManager {
  private transitionState: TransitionState = {
    isTransitioning: false,
    progress: 0,
    fromViewport: null,
    toViewport: null,
    startTime: 0,
    duration: 300,
  };

  private componentTransitions = new Map<string, ComponentTransitionData>();
  private animationFrameId: number | null = null;
  private transitionCallbacks: Array<(state: TransitionState) => void> = [];

  /**
   * Begin viewport transition
   *
   * Calculates transition paths for all components and initiates
   * the animation sequence.
   */
  async transitionToViewport(
    config: ViewportTransitionConfig
  ): Promise<void> {
    const {
      fromViewport,
      toViewport,
      components,
      duration = 300,
      easing = 'ease-in-out',
      preserveRelations = true,
    } = config;

    // Initialize transition state
    this.transitionState = {
      isTransitioning: true,
      progress: 0,
      fromViewport,
      toViewport,
      startTime: performance.now(),
      duration,
    };

    // Calculate transition data for each component
    this.componentTransitions.clear();

    components.forEach((component) => {
      const startPosition = component.position;
      const startSize = component.size;

      // Calculate target position and size in new viewport
      const { position: endPosition } = calculateResponsivePosition({
        basePosition: startPosition,
        baseViewport: fromViewport,
        targetViewport: toViewport,
        containerDimensions: { width: toViewport.width, height: toViewport.height },
      });

      const endSizeData = calculateResponsiveSize(
        startSize,
        fromViewport,
        toViewport
      );

      this.componentTransitions.set(component.id, {
        componentId: component.id,
        startPosition,
        endPosition,
        startSize,
        endSize: { width: endSizeData.width, height: endSizeData.height },
        currentPosition: startPosition,
        currentSize: startSize,
      });
    });

    // Apply CSS classes for transition
    this.applyTransitionStyles(easing, duration);

    // Start animation loop
    return new Promise((resolve) => {
      this.animate(resolve);
    });
  }

  /**
   * Animation loop using requestAnimationFrame
   *
   * Provides 60fps smooth transitions with easing functions.
   */
  private animate(onComplete: () => void): void {
    const tick = () => {
      const elapsed = performance.now() - this.transitionState.startTime;
      const progress = Math.min(elapsed / this.transitionState.duration, 1);

      this.transitionState.progress = progress;

      // Update component positions with easing
      this.updateComponentPositions(progress);

      // Notify listeners
      this.notifyTransitionProgress();

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(tick);
      } else {
        this.completeTransition();
        onComplete();
      }
    };

    this.animationFrameId = requestAnimationFrame(tick);
  }

  /**
   * Update component positions based on transition progress
   */
  private updateComponentPositions(progress: number): void {
    const easedProgress = this.applyEasing(progress);

    this.componentTransitions.forEach((transition) => {
      // Interpolate position
      transition.currentPosition = {
        x: this.lerp(
          transition.startPosition.x,
          transition.endPosition.x,
          easedProgress
        ),
        y: this.lerp(
          transition.startPosition.y,
          transition.endPosition.y,
          easedProgress
        ),
        z: transition.endPosition.z,
      };

      // Interpolate size
      transition.currentSize = {
        width: this.lerp(
          transition.startSize.width,
          transition.endSize.width,
          easedProgress
        ),
        height: this.lerp(
          transition.startSize.height,
          transition.endSize.height,
          easedProgress
        ),
      };

      // Update CSS custom properties
      if (this.transitionState.toViewport) {
        const cssProps = generateResponsiveCSSProperties(
          transition.componentId,
          transition.currentPosition,
          transition.currentSize,
          this.transitionState.toViewport
        );

        this.applyCSSProperties(transition.componentId, cssProps);
      }
    });
  }

  /**
   * Apply easing function to progress
   */
  private applyEasing(progress: number): number {
    // Ease-in-out cubic easing
    return progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
  }

  /**
   * Linear interpolation
   */
  private lerp(start: number, end: number, progress: number): number {
    return start + (end - start) * progress;
  }

  /**
   * Apply CSS transition styles to canvas
   */
  private applyTransitionStyles(easing: string, duration: number): void {
    const canvas = document.querySelector('[data-responsive-canvas]');
    if (canvas instanceof HTMLElement) {
      canvas.style.transition = `all ${duration}ms ${easing}`;
    }

    // Apply transition to all components
    this.componentTransitions.forEach((transition) => {
      const element = document.querySelector(
        `[data-component-id="${transition.componentId}"]`
      );
      if (element instanceof HTMLElement) {
        element.style.transition = `all ${duration}ms ${easing}`;
      }
    });
  }

  /**
   * Apply CSS custom properties to component element
   */
  private applyCSSProperties(
    componentId: string,
    properties: Record<string, string>
  ): void {
    const element = document.querySelector(
      `[data-component-id="${componentId}"]`
    );
    if (element instanceof HTMLElement) {
      Object.entries(properties).forEach(([key, value]) => {
        element.style.setProperty(key, value);
      });
    }
  }

  /**
   * Complete transition and cleanup
   */
  private completeTransition(): void {
    this.transitionState.isTransitioning = false;
    this.transitionState.progress = 1;

    // Remove transition styles
    const canvas = document.querySelector('[data-responsive-canvas]');
    if (canvas instanceof HTMLElement) {
      canvas.style.transition = '';
    }

    this.componentTransitions.forEach((transition) => {
      const element = document.querySelector(
        `[data-component-id="${transition.componentId}"]`
      );
      if (element instanceof HTMLElement) {
        element.style.transition = '';
      }
    });

    // Clear animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Cancel ongoing transition
   */
  cancelTransition(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.completeTransition();
  }

  /**
   * Subscribe to transition progress updates
   */
  onTransitionProgress(callback: (state: TransitionState) => void): () => void {
    this.transitionCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.transitionCallbacks.indexOf(callback);
      if (index > -1) {
        this.transitionCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Notify all transition listeners
   */
  private notifyTransitionProgress(): void {
    this.transitionCallbacks.forEach((callback) => {
      callback(this.transitionState);
    });
  }

  /**
   * Get current transition state
   */
  getTransitionState(): TransitionState {
    return { ...this.transitionState };
  }

  /**
   * Get component transition data
   */
  getComponentTransition(componentId: string): ComponentTransitionData | undefined {
    return this.componentTransitions.get(componentId);
  }

  /**
   * Preserve viewport state during transition
   *
   * Maintains scroll position, selection, and other viewport-specific state.
   */
  preserveViewportState(viewport: ResponsiveBreakpoint): {
    scrollPosition: { x: number; y: number };
    zoom: number;
    selectedComponents: string[];
  } {
    const canvas = document.querySelector('[data-responsive-canvas]');
    const scrollPosition = canvas instanceof HTMLElement
      ? { x: canvas.scrollLeft, y: canvas.scrollTop }
      : { x: 0, y: 0 };

    return {
      scrollPosition,
      zoom: 1, // Will be populated from editor store
      selectedComponents: [], // Will be populated from editor store
    };
  }

  /**
   * Restore viewport state after transition
   */
  restoreViewportState(state: {
    scrollPosition: { x: number; y: number };
    zoom: number;
    selectedComponents: string[];
  }): void {
    const canvas = document.querySelector('[data-responsive-canvas]');
    if (canvas instanceof HTMLElement) {
      canvas.scrollLeft = state.scrollPosition.x;
      canvas.scrollTop = state.scrollPosition.y;
    }
  }

  /**
   * Check if transition is active
   */
  isTransitioning(): boolean {
    return this.transitionState.isTransitioning;
  }
}

// Singleton instance for global access
export const viewportTransitionManager = new ViewportTransitionManager();
